import type { OperationsNode } from '../types/operations.types'
import { isTeamNode } from './nodeTypes'
import { findNodeById } from './findNodeById'
import { findNodeParent } from './findNodeParent'
import { resolveLandedOrder } from './resolveLandedOrder'

/** Parent node id -> that parent's child ids, in their dropped order. */
export type LocalOrder = Record<string, string[]>

const sameOrder = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index])

/**
 * The order to paint immediately after a drop, before the server confirms it.
 *
 * Covers BOTH directions: a cross-division drop has to REMOVE the team from the
 * source list and INSERT it into the destination, so two parents change at once.
 * An earlier version only reordered the destination's existing children, so a
 * cross-division drop found the dragged team absent, produced nothing, and the
 * row visibly snapped back to its old division until the refetch landed.
 *
 * Dropping onto a division row rather than onto a team appends — the only
 * possible gesture when the target division is empty or collapsed.
 *
 * Mirrors `resolveTreeDrop`'s guards exactly. Painting a preview for a drop the
 * resolver then refuses would show a move that never happens.
 */
export const resolveDropPreview = (
  nodes: OperationsNode[],
  activeId: string,
  overId: string,
  after = false
): LocalOrder => {
  if (activeId === overId) {
    return {}
  }

  const dragged = findNodeById(nodes, activeId)
  const over = findNodeById(nodes, overId)
  if (!dragged || !over || !isTeamNode(dragged)) {
    return {}
  }

  const source = findNodeParent(nodes, activeId)
  if (!source) {
    return {}
  }

  // Dropping onto a team targets that team's list; dropping onto a division row
  // targets the division itself.
  const destination = isTeamNode(over) ? findNodeParent(nodes, overId) : over

  // Only a division can hold teams — a conference or the season cannot, and
  // `resolveTreeDrop` rejects those drops.
  if (!destination || destination.type !== 'division') {
    return {}
  }

  // Dropping on the header of the team's own division is the overshoot that
  // `resolveTreeDrop` also refuses; painting it would flash a phantom move.
  if (!isTeamNode(over) && destination.id === source.id) {
    return {}
  }

  const sourceIds = source.children.map((child) => child.id)
  const destinationIds = destination.children.map((child) => child.id)

  // One rule for both directions — see `resolveLandedOrder`.
  const landed = resolveLandedOrder(destinationIds, activeId, overId, after)

  if (destination.id === source.id) {
    // A drop that changes nothing paints nothing.
    return sameOrder(landed, sourceIds) ? {} : { [source.id]: landed }
  }

  return {
    [source.id]: sourceIds.filter((id) => id !== activeId),
    [destination.id]: landed,
  }
}
